import { useCallback, useEffect, useState } from 'react'
import api from '../utils/api'
import { TimeWindow, TimeWindowFilter } from '@/components/TimeWindowFilter'
import { Button } from '@/components/Button'
import { SetupRequiredState } from '@/components/SetupRequiredState'
import { useToast } from '@/components/Toast'
import { apiErrorMessage, parseApiError, type ParsedApiError } from '@/utils/apiErrors'

interface Rec {
  id: string
  source: string
  category: string
  severity: string
  title: string
  body: string
  status: string
  email_status?: string
  created_at?: string
}

export default function BeaconRecommendations() {
  const { toast } = useToast()
  const [window, setWindow] = useState<TimeWindow>('week')
  const [rows, setRows] = useState<Rec[]>([])
  const [setupError, setSetupError] = useState<ParsedApiError | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/beacon/recommendations', { params: { window } })
      setRows(res.data.recommendations || [])
      setSetupError(null)
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete) setSetupError(parsed)
      else toast.error('Failed to load recommendations', parsed.message)
    }
  }, [window, toast])

  useEffect(() => { load() }, [load])

  const monitor = async () => {
    try {
      const res = await api.post('/api/v1/beacon/monitor')
      setSetupError(null)
      toast.success('Monitor', `${res.data.findings_created} finding(s); email=${res.data.email_status}`)
      await load()
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete) setSetupError(parsed)
      toast.error('Monitor failed', apiErrorMessage(err))
    }
  }

  const advise = async () => {
    try {
      await api.post('/api/v1/beacon/advise', { topic: 'security and vpc' })
      setSetupError(null)
      toast.success('Advisor ran')
      await load()
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete) setSetupError(parsed)
      toast.error('Advise failed', apiErrorMessage(err))
    }
  }

  const setStatus = async (id: string, status: string) => {
    await api.patch(`/api/v1/beacon/recommendations/${id}`, { status })
    await load()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">Recommendations & alerts</h2>
          <p className="text-sm text-slate-600">Advisor output and monitor findings (email stubbed if SES unset).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TimeWindowFilter value={window} onChange={setWindow} />
          <Button variant="secondary" onClick={advise}>Advise</Button>
          <Button onClick={monitor}>Run monitor</Button>
        </div>
      </div>

      {setupError && (
        <SetupRequiredState message={setupError.message} cta={setupError.cta} />
      )}

      <div className="space-y-3">
        {!setupError && rows.length === 0 && <p className="text-sm text-slate-400">No recommendations in this window.</p>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{r.severity}</span>
                  <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800">{r.source}</span>
                  <span className="text-xs text-slate-400">{r.category}</span>
                  {r.email_status && r.email_status !== 'none' && (
                    <span className="text-xs text-amber-700">email: {r.email_status}</span>
                  )}
                </div>
                <h3 className="mt-1 font-medium text-slate-900">{r.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{r.body}</p>
              </div>
              <div className="flex gap-2">
                {r.status === 'open' && (
                  <>
                    <Button variant="secondary" onClick={() => setStatus(r.id, 'acknowledged')}>Ack</Button>
                    <Button variant="secondary" onClick={() => setStatus(r.id, 'resolved')}>Resolve</Button>
                  </>
                )}
                <span className="self-center text-xs text-slate-400">{r.status}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
